/**
 * 3DConnexion SpaceMouse — Servicio principal
 *
 * Orquesta la comunicación con el SDK de 3DConnexion:
 * - Detecta disponibilidad del driver (NL-Proxy) via XHR
 * - Carga dinámicamente el SDK
 * - Crea un "viewport virtual" que alimenta al navlib
 * - Extrae deltas de traslación/rotación entre frames
 * - Emite eventos tipados (motion, button, connection, etc.)
 *
 * Uso:
 *   const service = SpaceMouseService.getInstance();
 *   service.configure({ appName: 'MiApp', debug: true });
 *   service.on('motion', (data) => sendToSocket(data));
 *   await service.connect();
 */

import { EventEmitter } from '../../helpers/EventEmitter';
import {
  SpaceMouseEvents,
  SpaceMouseConnectionState,
  SpaceMouseMotionData,
  SpaceMouseConfig,
  DEFAULT_SPACEMOUSE_CONFIG,
} from './types';
import {
  Mat4,
  identity,
  deltaTranslation,
  deltaRotation,
  lookAt,
} from './math-utils';
import {
  loadSpaceMouseSDK,
  get3DconnexionClass,
  isSDKLoaded,
  unloadSpaceMouseSDK,
} from './script-loader';

// ---------------------------------------------------------------------------
// NL-Proxy endpoint (driver local de 3DxWare)
// ---------------------------------------------------------------------------
const NL_PROXY_HOST = '127.51.68.120';
const NL_PROXY_PORT = 8181;
const NL_PROXY_URL = `https://${NL_PROXY_HOST}:${NL_PROXY_PORT}/3dconnexion/nlproxy`;

// ---------------------------------------------------------------------------
// Virtual viewport — bounding box mínimo para el navlib
// ---------------------------------------------------------------------------
const VIRTUAL_MODEL_SIZE = 10;
const VIRTUAL_MODEL_HALF = VIRTUAL_MODEL_SIZE / 2;
const VIRTUAL_FOV = Math.PI / 2;
const INITIAL_EYE_Z = VIRTUAL_MODEL_HALF / Math.tan(VIRTUAL_FOV / 2);

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export class SpaceMouseService extends EventEmitter<SpaceMouseEvents> {
  // Singleton
  private static instance: SpaceMouseService | null = null;

  // Estado
  private config: Required<SpaceMouseConfig>;
  private connectionState: SpaceMouseConnectionState = 'disconnected';
  private available = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  // SDK
  private spaceMouse: any = null;

  // Viewport virtual — estado de cámara
  private currentMatrix: Mat4 = identity();
  private initialMatrix: Mat4 = identity();
  private animating = false;
  private rafId: number | null = null;

  // ---------------------------------------------------------------------------
  // Singleton
  // ---------------------------------------------------------------------------

  static getInstance(): SpaceMouseService {
    if (!SpaceMouseService.instance) {
      SpaceMouseService.instance = new SpaceMouseService();
    }
    return SpaceMouseService.instance;
  }

  private constructor() {
    super();
    this.config = { ...DEFAULT_SPACEMOUSE_CONFIG } as Required<SpaceMouseConfig>;
    this.initViewportMatrix();
  }

  // ---------------------------------------------------------------------------
  // Configuración
  // ---------------------------------------------------------------------------

  configure(cfg: SpaceMouseConfig): void {
    this.config = { ...this.config, ...cfg };
  }

  getConnectionState(): SpaceMouseConnectionState {
    return this.connectionState;
  }

  isAvailable(): boolean {
    return this.available;
  }

  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  // ---------------------------------------------------------------------------
  // Detección del driver (check availability)
  // ---------------------------------------------------------------------------

  /**
   * Comprueba si el driver 3DxWare está corriendo localmente.
   * Hace una petición GET al NL-Proxy. Si responde, el driver está activo.
   */
  async checkAvailability(): Promise<boolean> {
    try {
      const response = await fetch(NL_PROXY_URL, {
        method: 'GET',
        headers: { Accept: 'application/json; charset=utf-8' },
        signal: AbortSignal.timeout(3000),
      });
      const wasAvailable = this.available;
      this.available = response.ok;

      if (wasAvailable !== this.available) {
        this.emit('availabilityChange', this.available);
      }

      return this.available;
    } catch {
      const wasAvailable = this.available;
      this.available = false;

      if (wasAvailable !== this.available) {
        this.emit('availabilityChange', this.available);
      }

      return false;
    }
  }

  /**
   * Iniciar polling periódico de disponibilidad.
   * Si autoConnect está activo, conecta automáticamente cuando detecte el driver.
   */
  startPolling(): void {
    this.stopPolling();

    if (this.config.pollInterval <= 0) return;

    // Comprobar inmediatamente
    this.checkAvailability().then((available) => {
      if (available && this.config.autoConnect && this.connectionState === 'disconnected') {
        this.connect();
      }
    });

    this.pollTimer = setInterval(async () => {
      const available = await this.checkAvailability();

      if (available && this.config.autoConnect && this.connectionState === 'disconnected') {
        this.connect();
      }
    }, this.config.pollInterval);
  }

  stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Conexión
  // ---------------------------------------------------------------------------

  /**
   * Conectar al ratón 3D:
   * 1. Cargar SDK dinámicamente
   * 2. Crear viewport virtual (div oculto con foco)
   * 3. Instanciar _3Dconnexion con el modelo de navegación
   * 4. Llamar connect() → onConnect → create3dmouse → on3dmouseCreated
   */
  async connect(): Promise<void> {
    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      return;
    }

    this.setConnectionState('connecting');

    try {
      // 1. Cargar SDK
      if (!isSDKLoaded()) {
        await loadSpaceMouseSDK();
      }

      // 2. Crear instancia SDK con el navigationModel
      // Usamos `window` como viewport para que el SDK NO robe foco
      // de los elementos de la web (inputs, botones, etc.).
      // El SDK soporta window como viewport explícitamente.
      const TDxClass = get3DconnexionClass();
      const navigationModel = this.buildNavigationModel();
      this.spaceMouse = new TDxClass(navigationModel);

      // 3. Conectar
      if (!this.spaceMouse.connect()) {
        throw new Error('Cannot connect to 3DConnexion NL-Proxy');
      }

      // La transición a 'connected' ocurre en onConnect → on3dmouseCreated
    } catch (err) {
      this.log('Connection error:', err);
      this.setConnectionState('error');
      this.emit('error', err instanceof Error ? err : new Error(String(err)));
    }
  }

  /**
   * Desconectar y limpiar
   */
  disconnect(): void {
    this.stopAnimation();

    if (this.spaceMouse) {
      try {
        this.spaceMouse.delete3dmouse();
      } catch (e) {
        this.log('Error deleting 3dmouse:', e);
      }
      this.spaceMouse = null;
    }

    this.setConnectionState('disconnected');
  }

  /**
   * Destruir servicio completamente (para cleanup)
   */
  destroy(): void {
    this.disconnect();
    this.stopPolling();
    unloadSpaceMouseSDK();
    SpaceMouseService.instance = null;
  }

  // ---------------------------------------------------------------------------
  // Navigation model — callbacks que el SDK invoca
  // ---------------------------------------------------------------------------

  private buildNavigationModel(): Record<string, any> {
    const self = this;

    return {
      // ---- Lifecycle ----
      onConnect() {
        self.log('Connected to NL-Proxy');
        if (self.spaceMouse) {
          // Usar `window` como viewport: el SDK detecta window === viewport
          // y asume foco sin robar el foco de elementos de la página.
          self.spaceMouse.create3dmouse(
            window,
            self.config.appName
          );
        }
      },

      onDisconnect(reason: string) {
        self.log('Disconnected:', reason);
        self.spaceMouse = null;
        self.setConnectionState('disconnected');
      },

      on3dmouseCreated() {
        self.log('3D mouse created');
        self.setConnectionState('connected');

        // Configurar como fuente de timing del cliente
        if (self.spaceMouse) {
          self.spaceMouse.update3dcontroller({
            frame: { timingSource: 1 },
          });
        }
      },

      // ---- Coordinate system ----
      getCoordinateSystem() {
        // X-derecha, Y-arriba, Z-hacia-fuera (right-handed)
        return [
          1, 0, 0, 0,
          0, 1, 0, 0,
          0, 0, 1, 0,
          0, 0, 0, 1,
        ];
      },

      // ---- Projection ----
      getPerspective() {
        return false; // Ortográfico — deltas lineales más fáciles de extraer
      },

      getFov() {
        return VIRTUAL_FOV;
      },

      getViewRotatable() {
        return true;
      },

      // ---- Model ----
      getModelExtents() {
        return [
          -VIRTUAL_MODEL_HALF, -VIRTUAL_MODEL_HALF, -VIRTUAL_MODEL_HALF,
          VIRTUAL_MODEL_HALF, VIRTUAL_MODEL_HALF, VIRTUAL_MODEL_HALF,
        ];
      },

      // ---- View ----
      getViewMatrix() {
        return [...self.currentMatrix];
      },

      getViewExtents() {
        return [
          -VIRTUAL_MODEL_HALF, -VIRTUAL_MODEL_HALF, -INITIAL_EYE_Z - 0.1,
          VIRTUAL_MODEL_HALF, VIRTUAL_MODEL_HALF, -INITIAL_EYE_Z + 0.1,
        ];
      },

      getViewTarget() {
        return [0, 0, 0];
      },

      getFrontView() {
        return lookAt([0, 0, INITIAL_EYE_Z], [0, 0, 0]);
      },

      // ---- Pointer (dummy — no UI real) ----
      getPointerPosition() {
        return [0, 0, 0];
      },

      // ---- Pivot ----
      getPivotPosition() {
        return [0, 0, 0];
      },

      // ---- Hit testing (no 3D scene) ----
      getLookAt() {
        return [0, 0, 0];
      },

      // ---- Write callbacks — AQUÍ capturamos los datos ----
      setViewMatrix(data: number[]) {
        const prevMatrix = self.currentMatrix;

        const [dx, dy, dz] = deltaTranslation(prevMatrix, data);
        const [rx, ry, rz] = deltaRotation(prevMatrix, data);

        // IMPORTANTE: Resetear la matriz a la posición inicial después de
        // extraer deltas. Esto evita que la cámara virtual derive (drift)
        // y asegura que cada frame del navlib parte del mismo estado base.
        // Sin este reset, el navlib acumula movimiento y la web se
        // "navega" como si fuera una escena 3D.
        self.currentMatrix = [...self.initialMatrix];

        // Filtrar frames sin movimiento
        const threshold = 1e-8;
        if (
          Math.abs(dx) > threshold || Math.abs(dy) > threshold || Math.abs(dz) > threshold ||
          Math.abs(rx) > threshold || Math.abs(ry) > threshold || Math.abs(rz) > threshold
        ) {
          const motionData: SpaceMouseMotionData = {
            dx, dy, dz,
            rx, ry, rz,
            timestamp: performance.now(),
          };
          self.emit('motion', motionData);
        }
      },

      setViewExtents(_extents: number[]) {
        // El zoom en ortográfico se maneja a través del cambio de extents
        // Incorporado ya en los deltas de dz vía matrix
      },

      setFov(_fov: number) {
        // No aplica en ortográfico virtual
      },

      setTarget(_target: number[]) {
        // No necesitamos target real
      },

      setTransaction(transaction: number) {
        if (transaction === 0 && !self.animating) {
          // Frame completado fuera de animación — forzar render
          self.requestRender();
        }
      },

      // ---- Motion start/stop ----
      onStartMotion() {
        self.log('Motion started');
        if (!self.animating) {
          self.animating = true;
          self.emit('motionStart', undefined as any);
          self.requestRender();
        }
      },

      onStopMotion() {
        self.log('Motion stopped');
        self.animating = false;
        self.emit('motionStop', undefined as any);
      },

      // ---- Commands / Buttons ----
      setActiveCommand(id: string) {
        self.log('Command:', id);
        self.emit('button', { button: id, type: 'command' });
      },

      setKeyPress(key: number) {
        self.log('Key press:', key);
        self.emit('button', { button: `KEY_${key}`, type: 'press' });
      },

      setKeyRelease(key: number) {
        self.log('Key release:', key);
        self.emit('button', { button: `KEY_${key}`, type: 'release' });
      },

      // ---- Properties that we don't use but must exist to avoid errors ----
      setMoving(_moving: boolean) { /* no-op */ },
      setPivotPosition(_pos: number[]) { /* no-op */ },
      setPivotVisible(_vis: boolean) { /* no-op */ },
      setLookFrom(_data: number[]) { /* no-op */ },
      setLookDirection(_data: number[]) { /* no-op */ },
      setLookAperture(_data: number) { /* no-op */ },
      setSelectionOnly(_data: boolean) { /* no-op */ },
      setSelectionAffine(_data: number[]) { /* no-op */ },
      setSettingsChanged(_data: any) { /* no-op */ },

      // ---- Optional read callbacks (return undefined = not supported) ----
      getConstructionPlane() { return undefined; },
      getFloorPlane() { return undefined; },
      getUnitsToMeters() { return 1.0; },
      getSelectionAffine() { return undefined; },
      getSelectionEmpty() { return true; },
      getSelectionExtents() { return undefined; },
      getViewFrustum() { return undefined; },
      getFrameTimingSource() { return 1; }, // Client-driven animation
      getFrameTime() { return performance.now(); },
    };
  }

  // ---------------------------------------------------------------------------
  // Animation loop
  // ---------------------------------------------------------------------------

  private requestRender(): void {
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame((time) => {
      this.rafId = null;

      if (this.spaceMouse && this.animating) {
        // Actualizar frame time para que el navlib procese el siguiente delta
        this.spaceMouse.update3dcontroller({
          frame: { time },
        });
        // Continuar loop mientras se esté moviendo
        this.requestRender();
      }
    });
  }

  private stopAnimation(): void {
    this.animating = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Camera matrix init
  // ---------------------------------------------------------------------------

  private initViewportMatrix(): void {
    // Cámara mirando al origen desde [0, 0, z]
    this.initialMatrix = lookAt([0, 0, INITIAL_EYE_Z], [0, 0, 0]);
    this.currentMatrix = [...this.initialMatrix];
  }

  /**
   * Resetear la cámara virtual a su posición inicial.
   * Útil si los deltas acumulados derivan demasiado.
   */
  resetCamera(): void {
    this.initViewportMatrix();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private setConnectionState(state: SpaceMouseConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.emit('connectionChange', state);
    }
  }

  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[SpaceMouse]', ...args);
    }
  }
}
